import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Flex,
  Grid,
  Icon,
  Item,
  ItemGroup,
  ItemSeparator,
  Markdown,
  Spinner,
  Text,
  Title,
} from '@kognitos/lattice';

import {
  AGENT_TEMPLATES,
  type IAgentTemplate,
  type ITemplateCard,
} from '../constants';
import { getTemplateAutomations } from '../template-automations';
import { loadAgentTemplate } from '../template-identity';
import { AgentRoleAvatar } from './hiring/AgentRoleAvatar';

const TEMPLATE_BY_ID = new Map(AGENT_TEMPLATES.map(card => [card.id, card]));

interface IAgentTemplatePreviewDialogProps {
  /** The card being previewed, or null when the dialog is closed. */
  card: ITemplateCard | null;
  onOpenChange: (open: boolean) => void;
  /** Create an agent from this role. */
  onUse: (card: ITemplateCard) => void;
  /** Switch the dialog to another role, e.g. one it works with. */
  onOpenRole?: (card: ITemplateCard) => void;
  /** A create is in flight (from this dialog or elsewhere). */
  isBusy?: boolean;
}

/** The authored markdown, clamped so its headings don't compete with the dialog's. */
const MARKDOWN_CLASS = [
  'w-full min-w-0 break-words leading-relaxed [&_*]:break-words',
  '[&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-sm',
  '[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold',
  '[&_h1]:mb-1 [&_h2]:mb-1 [&_h3]:mb-1 [&_h1]:mt-4 [&_h2]:mt-4 [&_h3]:mt-4',
  '[&>*:first-child]:mt-0',
  '[&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1 [&_ol]:pl-5 [&_ul]:pl-5',
  '[&_ul]:list-disc [&_ol]:list-decimal',
  '[&_strong]:font-medium [&_strong]:text-foreground',
].join(' ');

/** One labelled section of the résumé. */
function ProfileSection({
  label,
  children,
  'data-testid': dataTestId,
}: {
  label: string;
  children: ReactNode;
  'data-testid'?: string;
}) {
  return (
    <Flex
      direction="column"
      gap={2}
      className="w-full min-w-0"
      data-testid={dataTestId}
    >
      <Text type="eyebrow">{label}</Text>
      {children}
    </Flex>
  );
}

/** What the preview body tells its surrounding surface about loading. */
export interface IAgentTemplatePreviewState {
  /** The identity prose is still loading, so the role can't be hired yet. */
  isLoading: boolean;
  /** The identity prose failed to load; the role can't be hired. */
  hasError: boolean;
}

/**
 * A role's profile, written like a résumé. On the left, what the job is:
 * responsibilities, what it won't do, how it communicates, and its full
 * instructions on request. On the right, what it brings: its skills (the
 * automations it runs), the roles it works with, and the titles it goes by.
 *
 * The identity prose is fetched on mount; hovering a card prefetches it, so
 * it's usually already there.
 */
export function AgentTemplatePreview({
  card,
  footer,
  onStateChange,
  onOpenRole,
}: {
  card: ITemplateCard;
  /** Rendered under the body, given the loading state (e.g. the Hire action). */
  footer?: (state: IAgentTemplatePreviewState) => ReactNode;
  /** Reports the loading state to a surface that renders its action elsewhere. */
  onStateChange?: (state: IAgentTemplatePreviewState) => void;
  /** Opens one of the roles it works with. Omit to list them without links. */
  onOpenRole?: (card: ITemplateCard) => void;
}) {
  const { t } = useTranslation('agent-builder');
  const [template, setTemplate] = useState<IAgentTemplate | null>(null);
  const [hasError, setHasError] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  // Load the identity chunk for whichever card is open. Guarded with a
  // `cancelled` flag so switching cards quickly can't let a slow earlier fetch
  // overwrite the newer one.
  // eslint-disable-next-line no-restricted-syntax -- reason: fetch the lazily-chunked identity prose for the open card, with cancellation on card change
  useEffect(() => {
    let cancelled = false;
    setTemplate(null);
    setHasError(false);
    setShowInstructions(false);
    loadAgentTemplate(card)
      .then(full => {
        if (!cancelled) setTemplate(full);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [card]);

  // Distinct from `!template`: a failed load also leaves `template` null, and
  // that state must not spin forever — the body shows the error instead.
  const isLoadingTemplate = !template && !hasError;

  // eslint-disable-next-line no-restricted-syntax -- reason: report loading state to a parent that renders the action outside this body
  useEffect(() => {
    onStateChange?.({ isLoading: isLoadingTemplate, hasError });
  }, [onStateChange, isLoadingTemplate, hasError]);

  const automations = getTemplateAutomations(card);
  const partners = card.worksWith.flatMap(id => {
    const partner = TEMPLATE_BY_ID.get(id);
    return partner ? [partner] : [];
  });

  return (
    <>
      <Grid
        cols={1}
        className="min-h-0 flex-1 overflow-hidden lg:grid-cols-[1fr_20rem]"
      >
        {/* Left: the job. */}
        <Flex direction="column" className="min-h-0 overflow-y-auto px-6 py-6">
          <Flex direction="column" gap={8} className="w-full min-w-0">
            <ProfileSection label={t('agentBuilder.hiring.responsibilities')}>
              <Text className="leading-relaxed">{card.description}</Text>
            </ProfileSection>

            <ProfileSection
              label={t('agentBuilder.hiring.wontDo')}
              data-testid="template-preview-wont-do"
            >
              <Text className="leading-relaxed">{card.wontDo}</Text>
            </ProfileSection>

            {hasError ? (
              <Text color="muted">
                {t('agentBuilder.gallery.preview.loadError')}
              </Text>
            ) : !template ? (
              <Flex align="center" justify="center" className="w-full py-6">
                <Spinner size="lg" />
              </Flex>
            ) : (
              <>
                <ProfileSection
                  label={t('agentBuilder.gallery.preview.personality')}
                >
                  <Markdown
                    textProps={{ level: 'base', weight: 'normal' }}
                    className={MARKDOWN_CLASS}
                  >
                    {template.persona.replace(/^## Personality\s*/, '')}
                  </Markdown>
                </ProfileSection>

                <ProfileSection
                  label={t('agentBuilder.gallery.preview.instructions')}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-fit"
                    aria-expanded={showInstructions}
                    onClick={() => setShowInstructions(open => !open)}
                    data-testid="template-preview-toggle-instructions"
                  >
                    <Icon type="FileText" size="sm" />
                    {showInstructions
                      ? t('agentBuilder.hiring.hideInstructions')
                      : t('agentBuilder.hiring.showInstructions')}
                  </Button>
                  {showInstructions ? (
                    <Item
                      variant="muted"
                      data-testid="template-preview-instructions"
                    >
                      <Markdown
                        textProps={{ level: 'small', weight: 'normal' }}
                        className={MARKDOWN_CLASS}
                      >
                        {template.instructions}
                      </Markdown>
                    </Item>
                  ) : null}
                </ProfileSection>
              </>
            )}
          </Flex>
        </Flex>

        {/* Right: what it brings. Secondary, so a muted rail. */}
        <Flex
          direction="column"
          className="min-h-0 overflow-y-auto border-t border-border bg-muted/30 px-6 py-6 lg:border-l lg:border-t-0"
        >
          <Flex direction="column" gap={6} className="w-full min-w-0">
            <ProfileSection
              label={t('agentBuilder.hiring.skills', {
                count: automations.length,
              })}
              data-testid="template-preview-automations"
            >
              <ItemGroup className="w-full rounded-lg border">
                {automations.map((automation, index) => (
                  <Fragment key={automation.id}>
                    {index > 0 && <ItemSeparator />}
                    <Item size="sm">
                      <Text level="small" className="min-w-0 break-words">
                        {automation.name}
                      </Text>
                    </Item>
                  </Fragment>
                ))}
              </ItemGroup>
            </ProfileSection>

            {partners.length ? (
              <ProfileSection
                label={t('agentBuilder.hiring.worksWith')}
                data-testid="template-preview-works-with"
              >
                <Flex direction="column" gap={1.5} className="w-full min-w-0">
                  {partners.map(partner =>
                    onOpenRole ? (
                      <Button
                        key={partner.id}
                        variant="ghost"
                        size="sm"
                        className="-mx-2 h-auto w-full justify-start gap-2 px-2 py-1.5"
                        onClick={() => onOpenRole(partner)}
                        data-testid={`template-preview-partner-${partner.id}`}
                      >
                        <AgentRoleAvatar
                          group={partner.group}
                          title={partner.title}
                          size="sm"
                        />
                        <Text level="small" className="truncate">
                          {partner.title}
                        </Text>
                      </Button>
                    ) : (
                      <Flex key={partner.id} align="center" gap={2}>
                        <AgentRoleAvatar
                          group={partner.group}
                          title={partner.title}
                          size="sm"
                        />
                        <Text level="small" className="truncate">
                          {partner.title}
                        </Text>
                      </Flex>
                    ),
                  )}
                </Flex>
              </ProfileSection>
            ) : null}

            {card.alsoKnownAs.length ? (
              <ProfileSection
                label={t('agentBuilder.gallery.preview.alsoKnownAs')}
              >
                <Text level="small">{card.alsoKnownAs.join(', ')}</Text>
              </ProfileSection>
            ) : null}

            <ProfileSection label={t('agentBuilder.gallery.preview.voice')}>
              <Flex gap={2} className="flex-wrap">
                <Badge variant="secondary">
                  {t(`agentBuilder.config.tone.${card.tone}`)}
                </Badge>
                <Badge variant="secondary">
                  {t(`agentBuilder.config.style.${card.style}`)}
                </Badge>
              </Flex>
            </ProfileSection>

            {/* With a footer, the note sits beside the Hire action instead. */}
            {footer ? null : (
              <Text level="small" color="muted">
                {t('agentBuilder.hiring.editableNote')}
              </Text>
            )}
          </Flex>
        </Flex>
      </Grid>
      {footer?.({ isLoading: isLoadingTemplate, hasError })}
    </>
  );
}

/**
 * "Finance & Accounting · Procure-to-Pay", the line under a role's title.
 *
 * @param card - The role
 * @param t - An `agent-builder` translator
 * @returns The role's domain and team names
 */
export function roleTeamLine(
  card: ITemplateCard,
  t: TFunction<'agent-builder'>,
): string {
  return roleTeamParts(card, t).join(' · ');
}

/**
 * A role's domain and team names, in that order.
 *
 * @param card - The role
 * @param t - An `agent-builder` translator
 * @returns `[domain, team]`
 */
function roleTeamParts(
  card: ITemplateCard,
  t: TFunction<'agent-builder'>,
): [string, string] {
  return [
    t(`agentBuilder.catalog.domains.${card.domain}`),
    t(`agentBuilder.catalog.groups.${card.group}`),
  ];
}

/**
 * Where a role sits, as a breadcrumb: "Finance & Accounting › Procure-to-Pay".
 */
export function RoleBreadcrumb({ card }: { card: ITemplateCard }) {
  const { t } = useTranslation('agent-builder');
  const [domain, team] = roleTeamParts(card, t);
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>{domain}</BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>{team}</BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * Near-fullscreen profile of a role before hiring it.
 *
 * Hiring used to happen straight from a three-line card, which is a lot to
 * commit to sight-unseen when the role's value is several hundred words of
 * authored instructions. This shows the role like a résumé, with "Hire" as the
 * deliberate next step, so looking at several is cheap.
 */
export function AgentTemplatePreviewDialog({
  card,
  onOpenChange,
  onUse,
  onOpenRole,
  isBusy,
}: IAgentTemplatePreviewDialogProps) {
  const { t } = useTranslation('agent-builder');
  const [{ isLoading: isLoadingTemplate, hasError }, setPreviewState] =
    useState<IAgentTemplatePreviewState>({ isLoading: true, hasError: false });
  return (
    <Dialog open={Boolean(card)} onOpenChange={onOpenChange}>
      {/* Near-fullscreen: the point is to read the role without a
          scroll-in-a-scroll in a narrow modal. Capped for ultrawide displays. */}
      <DialogContent
        className="flex h-[92vh] w-[96vw] max-w-6xl flex-col gap-0 overflow-hidden p-0 sm:max-w-6xl"
        data-testid="template-preview-dialog"
      >
        <DialogHeader className="shrink-0 space-y-2 border-b border-border px-6 py-5 text-left">
          <Flex align="center" justify="between" gap={4} className="w-full">
            <Flex align="center" gap={4} className="min-w-0">
              {card && (
                <AgentRoleAvatar
                  group={card.group}
                  title={card.title}
                  size="lg"
                />
              )}
              <Flex direction="column" gap={1} className="min-w-0">
                <DialogTitle asChild>
                  <Title level="h3" className="break-words">
                    {card?.title ?? ''}
                  </Title>
                </DialogTitle>
                <DialogDescription asChild>
                  <Flex>{card && <RoleBreadcrumb card={card} />}</Flex>
                </DialogDescription>
              </Flex>
            </Flex>
            {/* The primary action sits in the header, always visible. It
                spins for both waits: the create in flight and the identity
                chunk still loading, and stays blocked on a failed load, since
                there would be nothing to seed the agent with. */}
            <Button
              className="mr-8 shrink-0"
              isLoading={isBusy || isLoadingTemplate}
              disabled={isBusy || isLoadingTemplate || hasError}
              onClick={() => card && onUse(card)}
              data-testid="template-preview-use"
            >
              {isBusy
                ? t('agentBuilder.gallery.preview.creating')
                : t('agentBuilder.hiring.hireNamed', {
                    title: card?.title ?? '',
                  })}
            </Button>
          </Flex>
        </DialogHeader>

        {card && (
          <AgentTemplatePreview
            card={card}
            onStateChange={setPreviewState}
            onOpenRole={onOpenRole}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
